import { fetchFxRate } from "../api";
import { AdvanceAmount, ClaimMethod, ExpenseLineItem, FxOverride } from "../types";
import { buildFxLedger, fetchLedgerFxRates, pairKey } from "./fx";

export interface SettlementAmount {
  currency: string;
  amount: number;
  direction: "payable" | "owed";
}

/** "INR 37,966.01" (always the magnitude - direction is shown separately,
 *  same split CurrencyConversionCard's own tiles already use). */
export function formatSettlementAmount(value: SettlementAmount): string {
  return `${value.currency} ${value.amount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

/** What the "Settlement amount" table column shows: the same net-payable
 *  figure CurrencyConversionCard's bottom tile(s) show in the Request
 *  Details dialog, always projected (never the actual settledAmount, even
 *  once Completed - see this module's own callers).
 *
 *  Nets into whichever currency `settlementTargetByCurrency` (the saved
 *  per-row choices from CurrencyConversionCard's own settlement-currency
 *  pickers, persisted on the request document - see types.ts) already
 *  agrees on for every currency actually involved, so the column matches
 *  whatever the employee last picked in the reconciliation table itself
 *  rather than always silently reverting to the request's own currency
 *  (confirmed live: every row's settlement currency saved as INR still
 *  showed "SGD 1,354.17" here while the Request Details dialog's own Net
 *  payable tile correctly showed "INR 1,00,658.28" for the same request -
 *  this column was ignoring the saved choice entirely). Falls back to
 *  `currency` (the request's own) when nothing's saved, or when the saved
 *  choices don't all agree on one currency - same reasoning
 *  CurrencyConversionCard's own Net payable tile uses for its fallback. */
export interface SettlementAmountInput {
  claimMethod: ClaimMethod;
  currency: string;
  expenses: ExpenseLineItem[];
  estimatedAllowanceAmount: number;
  /** The trip's departure date (Other reimbursements fall back to
   *  createdAt, same as CurrencyConversionCard's own caller) - the
   *  advance's own conversion date, since AdvanceAmount carries no date of
   *  its own. */
  departureDate: string;
  /** Saved per-currency settlement-currency choices (see
   *  TravelRequestSummary.settlementTargetByCurrency) - absent/empty nets
   *  everything into `currency` as before. */
  settlementTargetByCurrency?: Record<string, string>;
  /** Saved FX overrides (see TravelRequestSummary.fxOverrides) - a currency
   *  pair with a saved override uses THAT rate instead of a freshly-fetched
   *  one (same priority buildFxLedger already gives it). Without this, the
   *  column quietly recomputed every pair from live rates even when the
   *  Request Details dialog had a custom override on file, so the two could
   *  show different numbers for the same request (confirmed live: an IRR
   *  row with "1 IRR = 1.124 ANG" overridden showed "ANG 562" in the dialog
   *  but "ANG 0" here, since the override was never even looked at). */
  fxOverrides?: FxOverride[];
}

/** The one currency every involved line should net into - every entry of
 *  `settlementTargetByCurrency` that's actually relevant here (an advance
 *  currency, or `currency` itself for the allowance) agreeing on one
 *  currency, or `fallback` otherwise. A currency with no saved entry
 *  defaults to `fallback` too (same default CurrencyConversionCard's own
 *  per-row picker uses), so a request where only SOME rows were ever
 *  explicitly repointed still resolves correctly once the rest already
 *  matched the fallback to begin with. */
function resolveNetCurrency(
  relevantCurrencies: string[],
  settlementTargetByCurrency: Record<string, string> | undefined,
  fallback: string
): string {
  const targets = new Set(
    relevantCurrencies.map((c) => settlementTargetByCurrency?.[c] ?? fallback)
  );
  return targets.size === 1 ? [...targets][0] : fallback;
}

/** Fixed Allowance's own payable figure(s): the allowance folded in as a
 *  synthetic bill in `input.currency` (same trick CurrencyConversionCard.tsx,
 *  notifications.py and reports/request_report.py all use), then the SAME
 *  multi-currency ledger reimbursementSettlement builds below - each
 *  currency converted into ITS OWN saved settlement target, never pooled
 *  into one currency.
 *
 *  Confirmed desired behaviour (client org's own reimbursement policy): a
 *  Fixed Allowance employee is paid the fixed allowance AND reimbursed for
 *  whatever they actually spent (e.g. a USD 160 allowance plus USD 100 of
 *  uploaded bills pays out USD 260) - bills are no longer tracking-only.
 *
 *  Previously this forcibly resolved every currency to ONE netCurrency and
 *  converted everything (including bills in other currencies) into it via a
 *  fresh live rate - confirmed live: an AFN bill explicitly left settling in
 *  AFN (its own saved settlementTargetByCurrency entry) still got silently
 *  re-converted into ALL here, producing "ALL 9,694.51" when the Request
 *  Details dialog's own per-row tiles showed "AFN 520" and "ALL 9,043" -
 *  neither of which matched this column's number at all. Now returns one
 *  entry per distinct target currency instead, exactly matching
 *  CurrencyConversionCard.tsx's own "Net payable (<currency>)" tiles (which
 *  likewise fold the allowance into the defaultTargetCurrency row rather
 *  than computing a second, separate pooled figure) - only when every
 *  relevant currency's saved target genuinely agrees on one currency does
 *  this collapse to a single entry, same as Reimbursement's own figure. */
async function fixedAllowanceSettlement(
  input: SettlementAmountInput,
  advanceAmounts: AdvanceAmount[]
): Promise<SettlementAmount[] | null> {
  const allowanceLine: ExpenseLineItem = {
    id: "__allowance__",
    category: "Allowance",
    amount: input.estimatedAllowanceAmount,
    currency: input.currency,
    vendor: null,
    description: null,
    date: input.departureDate || null,
    blobPath: null,
    filename: null,
    contentType: null,
    uploadedAt: input.departureDate || ""
  };
  const expenses = input.estimatedAllowanceAmount ? [...input.expenses, allowanceLine] : input.expenses;
  const netCurrency = resolveNetCurrency(
    [...expenses.map((e) => e.currency || input.currency), ...advanceAmounts.map((a) => a.currency)],
    input.settlementTargetByCurrency,
    input.currency
  );
  const targetCurrencyByCurrency = new Map<string, string>();
  for (const e of expenses) {
    const cur = e.currency || input.currency;
    targetCurrencyByCurrency.set(cur, input.settlementTargetByCurrency?.[cur] ?? input.currency);
  }
  for (const a of advanceAmounts) {
    targetCurrencyByCurrency.set(a.currency, input.settlementTargetByCurrency?.[a.currency] ?? input.currency);
  }
  const rates = await fetchLedgerFxRates(expenses, advanceAmounts, input.currency, targetCurrencyByCurrency, input.departureDate);
  const ledger = buildFxLedger(
    expenses,
    advanceAmounts,
    input.currency,
    netCurrency,
    targetCurrencyByCurrency,
    rates,
    new Map(),
    input.fxOverrides ?? [],
    input.departureDate
  );
  const toEntry = (currency: string, amount: number): SettlementAmount => ({
    currency,
    amount: Math.abs(amount),
    direction: amount >= 0 ? "payable" : "owed"
  });
  const entries: SettlementAmount[] = [
    ...ledger.netPayments.map((net) => toEntry(net.currency, net.amount)),
    ...ledger.unconvertedOffsets.map((off) => toEntry(off.currency, off.amount))
  ];
  return entries.length > 0 ? entries : null;
}

/** Reimbursement's own payable figure(s): the same multi-currency ledger the
 *  Request Details dialog builds. When every bill/advance currency's saved
 *  settlement choice agrees on one currency, this is exactly one number, in
 *  that currency - the common case. When they're split across different
 *  targets (an employee deliberately pointed different rows at different
 *  settlement currencies), there is no single meaningful combined figure
 *  (same reasoning Fixed Allowance's Net payable tile already follows for
 *  ITS OWN split-target case - see fixedAllowanceSettlement), so this
 *  returns ledger.netPayments AS IS: one entry per distinct target
 *  currency, exactly matching CurrencyConversionCard's own
 *  "Net payable (<currency>)" tiles, one per currency, rather than
 *  collapsing them into a single number in the request's own currency
 *  (confirmed live: two rows pointed at BIF and INR respectively showed
 *  two separate tiles in the wizard - "Net payable (BIF)" and
 *  "Net payable (INR)" - but this column previously showed one number,
 *  "IRR 96,89,500", silently re-converting everything into the request's
 *  own IRR instead of reflecting either actual choice). A currency left
 *  unconverted (no rate available) is surfaced as its own entry too (same
 *  reasoning as a multi-target split - "left out of a number" would be a
 *  silent, wrong total), not dropped as the single-target path used to. */
async function reimbursementSettlement(
  input: SettlementAmountInput,
  advanceAmounts: AdvanceAmount[]
): Promise<SettlementAmount[] | null> {
  const netCurrency = resolveNetCurrency(
    [...input.expenses.map((e) => e.currency || input.currency), ...advanceAmounts.map((a) => a.currency)],
    input.settlementTargetByCurrency,
    input.currency
  );
  // Split targets: build the ledger using EACH currency's own saved target
  // (not netCurrency, which is only meaningful once everything agrees) -
  // exactly CurrencyConversionCard's own per-row targetCurrencyByCurrency,
  // reconstructed here the same way resolveNetCurrency itself reads it.
  const targetCurrencyByCurrency = new Map<string, string>();
  for (const e of input.expenses) {
    const cur = e.currency || input.currency;
    targetCurrencyByCurrency.set(cur, input.settlementTargetByCurrency?.[cur] ?? input.currency);
  }
  for (const a of advanceAmounts) {
    targetCurrencyByCurrency.set(a.currency, input.settlementTargetByCurrency?.[a.currency] ?? input.currency);
  }
  const rates = await fetchLedgerFxRates(input.expenses, advanceAmounts, input.currency, targetCurrencyByCurrency, input.departureDate);
  const ledger = buildFxLedger(
    input.expenses,
    advanceAmounts,
    input.currency,
    netCurrency,
    targetCurrencyByCurrency,
    rates,
    new Map(),
    input.fxOverrides ?? [],
    input.departureDate
  );
  const toEntry = (currency: string, amount: number): SettlementAmount => ({
    currency,
    amount: Math.abs(amount),
    direction: amount >= 0 ? "payable" : "owed"
  });
  const entries: SettlementAmount[] = [
    ...ledger.netPayments.map((net) => toEntry(net.currency, net.amount)),
    ...ledger.unconvertedOffsets.map((off) => toEntry(off.currency, off.amount))
  ];
  return entries.length > 0 ? entries : null;
}

/** Entry point for the "Settlement amount" column - dispatches on claim
 *  method, same branch CurrencyConversionCard's own tile rendering uses.
 *  Both claim methods may return several entries when rows target different
 *  currencies (see fixedAllowanceSettlement/reimbursementSettlement's own
 *  doc comments) - a single entry is just the common case where everything
 *  agrees on one settlement currency. */
export async function computeSettlementAmount(
  input: SettlementAmountInput,
  advanceAmounts: AdvanceAmount[]
): Promise<SettlementAmount[] | null> {
  if (input.claimMethod === "fixed_allowance") {
    return fixedAllowanceSettlement(input, advanceAmounts);
  }
  return reimbursementSettlement(input, advanceAmounts);
}

/** One line of the "Net payable" breakdown a new user can read without
 *  having to reverse-engineer the single final number - see
 *  NetPayableBreakdown below. */
export interface NetPayableLine {
  label: string;
  /** Already formatted (e.g. "SGD 60 → EUR 60 × 1.4305 = SGD 85.83" for a
   *  converted advance line, "SGD 1,500" for the allowance itself when no
   *  conversion was needed) - built here rather than handing back raw
   *  numbers, since each line's own wording differs (a straight amount vs. a
   *  "from → to" conversion). */
  value: string;
}

/** Fixed Allowance's "Net payable" tile, explained: one line for the
 *  allowance (converted into netCurrency if needed), one per advance
 *  currency (converted the same way), and the final subtraction - exactly
 *  the three things computeSettlementAmount/fixedAllowanceSettlement
 *  compute internally but never exposes, since that function only ever
 *  needs to hand back the one final number. Returns null on the same
 *  "couldn't get a rate" condition fixedAllowanceSettlement itself falls
 *  back to null for - no breakdown to show when the figure itself
 *  couldn't be computed. */
export async function explainFixedAllowanceNetPayable(
  input: SettlementAmountInput,
  advanceAmounts: AdvanceAmount[],
  netCurrency: string
): Promise<{ lines: NetPayableLine[]; result: SettlementAmount } | null> {
  // A saved override for this exact (currency, netCurrency) pair wins over
  // a freshly-fetched rate - same priority buildFxLedger itself gives one
  // (see lib/fx.ts), checked here too since this function computes its own
  // rates directly rather than going through buildFxLedger. Without this,
  // an advance-only row's saved "Override FX" (same button/dialog every
  // other row has - available regardless of claim method) was silently
  // ignored, so this tile's own live figure could disagree with the ledger
  // table's own Valuation column immediately above it for that same row.
  const overrideRate = (currency: string): number | null =>
    input.fxOverrides?.find((o) => pairKey(o.currency, o.targetCurrency) === pairKey(currency, netCurrency))?.rate ??
    null;
  const allowanceRate =
    netCurrency === input.currency
      ? 1
      : overrideRate(input.currency) ??
        (await fetchFxRate(input.currency, netCurrency, input.departureDate.slice(0, 10)).catch(() => null));
  if (allowanceRate === null) return null;
  const allowanceInTarget = input.estimatedAllowanceAmount * allowanceRate;

  const lines: NetPayableLine[] = [
    {
      label: "Allowance",
      value:
        allowanceRate === 1
          ? formatSettlementAmount({ currency: input.currency, amount: input.estimatedAllowanceAmount, direction: "payable" })
          : `${formatSettlementAmount({ currency: input.currency, amount: input.estimatedAllowanceAmount, direction: "payable" })} × ${formatRateForBreakdown(allowanceRate)} = ${formatSettlementAmount({ currency: netCurrency, amount: allowanceInTarget, direction: "payable" })}`
    }
  ];

  // Actual spend is now additive to the allowance (confirmed desired
  // behaviour - see fixedAllowanceSettlement's own doc comment), so it gets
  // its own breakdown line(s) here too, one per bill currency, same
  // treatment as the advance lines below (summed, with a per-currency
  // conversion shown when a rate was actually needed).
  const spentByCurrency = new Map<string, number>();
  for (const e of input.expenses) {
    const cur = e.currency || input.currency;
    spentByCurrency.set(cur, (spentByCurrency.get(cur) ?? 0) + e.amount);
  }
  let spentInNetCurrency = 0;
  for (const [cur, amount] of spentByCurrency) {
    if (amount === 0) continue;
    const rate =
      cur === netCurrency
        ? 1
        : overrideRate(cur) ?? (await fetchFxRate(cur, netCurrency, input.departureDate.slice(0, 10)).catch(() => null));
    if (rate === null) continue; // same "leave it out rather than fail the whole figure" rule fixedAllowanceSettlement follows via the ledger's own unconverted-row handling
    const inTarget = amount * rate;
    spentInNetCurrency += inTarget;
    lines.push({
      label: "Actual expense",
      value:
        rate === 1
          ? formatSettlementAmount({ currency: cur, amount, direction: "payable" })
          : `${formatSettlementAmount({ currency: cur, amount, direction: "payable" })} × ${formatRateForBreakdown(rate)} = ${formatSettlementAmount({ currency: netCurrency, amount: inTarget, direction: "payable" })}`
    });
  }

  let advancedInNetCurrency = 0;
  for (const a of advanceAmounts) {
    if (a.amount === 0) continue;
    const rate =
      a.currency === netCurrency
        ? 1
        : overrideRate(a.currency) ??
          (await fetchFxRate(a.currency, netCurrency, input.departureDate.slice(0, 10)).catch(() => null));
    if (rate === null) continue; // same "leave it out rather than fail the whole figure" rule fixedAllowanceSettlement follows via the ledger's own unconverted-row handling
    const inTarget = a.amount * rate;
    advancedInNetCurrency += inTarget;
    lines.push({
      label: "Advance received",
      value:
        rate === 1
          ? formatSettlementAmount({ currency: a.currency, amount: a.amount, direction: "payable" })
          : `${formatSettlementAmount({ currency: a.currency, amount: a.amount, direction: "payable" })} × ${formatRateForBreakdown(rate)} = ${formatSettlementAmount({ currency: netCurrency, amount: inTarget, direction: "payable" })}`
    });
  }

  const diff = allowanceInTarget + spentInNetCurrency - advancedInNetCurrency;
  const result: SettlementAmount = {
    currency: netCurrency,
    amount: Math.abs(diff),
    direction: diff >= 0 ? "payable" : "owed"
  };
  lines.push({
    label: result.direction === "payable" ? "Net payable" : "Amount owed back",
    value: `${formatSettlementAmount({ currency: netCurrency, amount: allowanceInTarget, direction: "payable" })} + ${formatSettlementAmount({ currency: netCurrency, amount: spentInNetCurrency, direction: "payable" })} − ${formatSettlementAmount({ currency: netCurrency, amount: advancedInNetCurrency, direction: "payable" })} = ${formatSettlementAmount(result)}`
  });

  return { lines, result };
}

/** A rate for the breakdown popover - matches CurrencyConversionCard's own
 *  formatRate (4 significant digits below 1, so a weak currency's rate
 *  doesn't collapse to "0.0000"), duplicated here rather than imported
 *  since that one lives in a component file, not a lib one. */
function formatRateForBreakdown(rate: number): string {
  return rate >= 1
    ? rate.toLocaleString(undefined, { maximumFractionDigits: 4 })
    : rate.toLocaleString(undefined, { maximumSignificantDigits: 4 });
}
